import React, { useEffect, useState } from "react";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import EmpSuccess from "../employees/employeeSuccess";
import "../employees/index.css";
import { createDirectorsLoanCapital, getDirectorsLoanCapitalById, updateLoanCapitalById } from "../../../apis/directors";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { Moment } from "moment";
import PredefinedDateRanges from "../../../core/common/datePicker";


const initialValues = {
    capital: "",
    directorId: "",
    loanAmount: "",
    loanInterestRate: "",
    loanDate: "",
};

type CapitalResponse = {
    id: number;
    directorId: number;
    capital: number;
    loanAmount: number;
    loanInterestRate: number;
    loanDate: string;
    paymentDetails: string; // JSON string
};
type CapitalData = {
    key: number;
    directorId: number;
    capital: number;
    loanAmount: number;
    loanInterestRate: number;
    loanDate: string;
    paymentDetails: string;
    id: number;
};



const DirectorCapital = () => {
    const { search } = useLocation();
    const id = new URLSearchParams(search).get("id");

    const [allCapitalData, setCapitalData] = useState<CapitalData[]>([]);
    const [successCreation, setSuccessCreation] = useState<boolean>(false);
    const [formData, setFormData] = useState(initialValues);
    const [selectedLoan, setSelectedLoan] = useState<any>(null);
    const [payments, setPayments] = useState<any[]>([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
    const [showAddCapitalModal, setShowAddCapitalModal] = useState(false);
    const [paymentForm, setPaymentForm] = useState({
        amount: "",
        paymentType: "interest",
        date_time: "",
    });

    const handlePaymentChange = (e) => {
        const { name, value } = e.target;
        setPaymentForm({ ...paymentForm, [name]: value });
    };


    const [states, setState] = useState<{
        start: Moment | undefined;
        end: Moment | undefined;
    }>({
        start: undefined,
        end: undefined,
    });
    const { start, end } = states;


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const calculateInterest = (
        loanAmount: number,
        rate: number,
        loanDate: string
    ) => {
        if (!loanAmount || !rate || !loanDate) return 0;
        const loanStart = moment(loanDate);
        const calculationEnd = end ? moment(end) : moment();
        const days = calculationEnd.diff(loanStart, "days");
        if (days <= 0) return 0;
        const years = days / 365;
        return (loanAmount * rate * years) / 100;
    };

    const getTotalInterest = () => {
        return allCapitalData.reduce((total, item) => {
            return (
                total +
                calculateInterest(
                    item.loanAmount,
                    item.loanInterestRate,
                    item.loanDate
                )
            );
        }, 0);
    };

    const fetchAllCapital = async () => {
        try {
            const response = await getDirectorsLoanCapitalById(id);

            let capitalData = response?.data || [];
            if (start && end) {
                capitalData = capitalData.filter((item: CapitalResponse) => {
                    const loanDate = moment(item.loanDate);
                    return loanDate.isBetween(start, end, "day", "[]");
                });
            }

            const formatted = capitalData.map((item: CapitalResponse, i: number) => ({
                key: i + 1,
                directorId: item.directorId,
                capital: item.capital,
                loanAmount: item.loanAmount,
                loanInterestRate: item.loanInterestRate,
                loanDate: item.loanDate,
                paymentDetails: item.paymentDetails,
                id: item.id,
            }));

            setCapitalData(formatted);
        } catch (error) {
            console.error("Error fetching capital data:", error);
        }
    };
    
    const getTotalLoan = () => {
        return allCapitalData.reduce((total, item) => {
            return total + Number(item.loanAmount || 0);
        }, 0);
    };

    const getTotalCapital = () => {
        return allCapitalData.reduce((total, item) => {
            return total + Number(item.capital || 0);
        }, 0);
    };

const handleViewPayments = (record: any) => {
  setSelectedLoan(record);

  try {
    let parsed = record.paymentDetails;
    // 🔥 Keep parsing until it becomes object/array
    while (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }

    // Ensure array
    const safePayments = Array.isArray(parsed) ? parsed : [];

    console.log("FINAL PARSED:", safePayments);

    setPayments(safePayments);
    setShowPaymentModal(true);
  } catch (err) {
    console.error("Invalid JSON", err);
    setPayments([]);
  }
};

    useEffect(() => {
        if (id) {
            fetchAllCapital();
        }
    }, [states, id]);

    const handleAddPayment = (record: any) => {
        setSelectedLoan(record);
        setShowAddPaymentModal(true);
    };

    const getPaidInterest = (paymentDetails: string) => {
        try {
            const payments = paymentDetails ? JSON.parse(paymentDetails) : [];

            return payments
                .filter((p: any) => p.paymentType === "interest")
                .reduce(
                    (sum: number, p: any) =>
                        sum + Number(p.netAmount || p.amount || 0),
                    0
                );
        } catch {
            return 0;
        }
    };

    const getRemainingLoan = (record: CapitalData) => {
        try {
            const payments = record.paymentDetails
                ? JSON.parse(record.paymentDetails)
                : [];

            const paidPrincipal = payments
                .filter((p: any) => p.paymentType === "principal")
                .reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);

            return record.loanAmount - paidPrincipal;
        } catch {
            return record.loanAmount;
        }
    };

    const getBalanceInterest = (record: CapitalData) => {
        const totalInterest = calculateInterest(
            record.loanAmount,
            record.loanInterestRate,
            record.loanDate
        );

        const paidInterest = getPaidInterest(record.paymentDetails);

        return totalInterest - paidInterest;
    };

    const getTotalPaidInterest = () => {
        return allCapitalData.reduce(
            (total, item) => total + getPaidInterest(item.paymentDetails),
            0
        );
    };

    const getTotalBalanceInterest = () => {
        return getTotalInterest() - getTotalPaidInterest();
    };

    const getTotalRemainingLoan = () => {
        return allCapitalData.reduce(
            (total, item) => total + getRemainingLoan(item),
            0
        );
    };

    const columns = [
        {
            title: "S.No",
            dataIndex: "key",
        },
        {
            title: "Date",
            dataIndex: "loanDate",
            render: (loanDate: string) => moment(loanDate).format("DD-MM-YYYY"),
        },
        {
            title: "Capital",
            dataIndex: "capital",
        },
        {
            title: "Loan Amount",
            dataIndex: "loanAmount",
        },
        {
            title: "Interest (%)",
            dataIndex: "loanInterestRate",
        },
        {
            title: "Total Interest",
            dataIndex: "interest",
            render: (_: any, record: CapitalData) => {
                const interest = calculateInterest(
                    record.loanAmount,
                    record.loanInterestRate,
                    record.loanDate
                );
                return interest;
            },
        },
        {
            title: "Paid Interest",
            render: (_: any, record: CapitalData) => {
                return getPaidInterest(record.paymentDetails);
            },
        },
        {
            title: "Balance Interest",
            render: (_: any, record: CapitalData) => {
                return getBalanceInterest(record);
            },
        },
        {
            title: "Remaining Loan",
            render: (_: any, record: CapitalData) => {
                return getRemainingLoan(record);
            },
        },
        {
            title: "Actions",
            dataIndex: "actions",
            render: (_: any, record: CapitalData) => (
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-sm btn-info"
                        onClick={() => handleViewPayments(record)}
                    >
                        View Payments
                    </button>

                    <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleAddPayment(record)}
                    >
                        Add Payment
                    </button>
                </div>
            ),
        }
    ];

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const payload = {
            directorId: Number(id), // from URL
            capital: Number(formData.capital),
            loanAmount: Number(formData.loanAmount),
            loanInterestRate: Number(formData.loanInterestRate),
            loanDate: formData.loanDate,
        };

        try {
            const response = await createDirectorsLoanCapital(payload);
            if (response.status === true) {
                setSuccessCreation(true);
                setShowAddCapitalModal(false);
                fetchAllCapital();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getAllPayments = () => {
        let allPayments: any[] = [];

        allCapitalData.forEach((loan) => {
            try {
                const payments = loan.paymentDetails
                    ? JSON.parse(loan.paymentDetails)
                    : [];

                payments.forEach((p: any, index: number) => {
                    allPayments.push({
                        key: `${loan.id}-${index}`,
                        loanId: loan.id,
                        date: p.date_time,
                        type: p.paymentType,
                        amount: p.amount,
                    });
                });
            } catch (err) {
                console.error("Invalid JSON", err);
            }
        });

        return allPayments;
    };

    const paymentColumns = [
        {
            title: "S.No",
            render: (_: any, __: any, index: number) => index + 1,
        },
        {
            title: "Loan ID",
            dataIndex: "loanId",
        },
        {
            title: "Date",
            dataIndex: "date",
            render: (date: string) => moment(date).format("DD-MM-YYYY"),
        },
        {
            title: "Type",
            dataIndex: "type",
        },
        {
            title: "Amount",
            dataIndex: "amount",
            render: (amt: number) => `₹ ${amt}`,
        },
    ];

    const handleSavePayment = async () => {
        if (!selectedLoan) return;

        let amount = Number(paymentForm.amount || 0);
        let tds = 0;
        let netAmount = amount;

        // ✅ TDS only for interest
        if (paymentForm.paymentType === "interest") {
            tds = (amount * 10) / 100;
            netAmount = amount - tds;
        }

        const newPayment = {
            amount,
            paymentType: paymentForm.paymentType,
            date_time: paymentForm.date_time,
            tds,
            netAmount,
        };

        let existingPayments = [];

        try {
            existingPayments = selectedLoan.paymentDetails
                ? JSON.parse(selectedLoan.paymentDetails)
                : [];
        } catch {
            existingPayments = [];
        }

        const updatedPayments = [...existingPayments, newPayment];

        const payload = {
            paymentDetails: JSON.stringify(updatedPayments),
        };

        try {
            await updateLoanCapitalById(selectedLoan.id, payload);

            // ✅ refresh data from backend
            fetchAllCapital();

            setShowAddPaymentModal(false);

            setPaymentForm({
                amount: "",
                paymentType: "interest",
                date_time: "",
            });
        } catch (error) {
            console.error("Payment update failed", error);
        }
    };

    const handleViewAllPayments = () => {
  try {
    let allPayments: any[] = [];

    allCapitalData.forEach((record: any) => {
      let parsed = record.paymentDetails;

      // 🛑 Skip empty / null values
      if (!parsed || parsed === "" || parsed === "null") return;

      try {
        // First parse
        if (typeof parsed === "string") {
          parsed = JSON.parse(parsed);
        }

        // Second parse (double string case)
        if (typeof parsed === "string") {
          parsed = JSON.parse(parsed);
        }

      } catch (e) {
        console.warn("Skipping invalid JSON:", parsed);
        return; // skip bad record
      }

      if (Array.isArray(parsed)) {
        allPayments.push(
          ...parsed.map((p: any) => ({
            ...p,
            loanId: record.id || "-",
            loanAmount: record.loanAmount || 0,
          }))
        );
      }
    });

    // Sort latest first
    allPayments.sort(
      (a, b) =>
        new Date(b.date_time || b.date).getTime() -
        new Date(a.date_time || a.date).getTime()
    );

    setPayments(allPayments);
    setShowPaymentModal(true);

  } catch (err) {
    console.error("Final error:", err);
    setPayments([]);
  }
};
    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2"></div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            <div className="mb-2">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        console.log("Clicked");
                                        setShowAddCapitalModal(true);
                                    }}
                                >
                                    + Add Capital / Loan
                                </button>
                                <button
  className="btn btn-primary ms-2"
  onClick={handleViewAllPayments}
>
  View All Payments
</button>
                            </div>
                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                                <div className="me-3">
                                    <div>
                                        <label style={{ color: "#000" }}>Search</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm me-2"
                                            placeholder="Search by Name\Mobile"
                                            style={{
                                                backgroundColor: "#b4b4b447",
                                                padding: "0.2rem 0.85rem",
                                                marginTop: "5px",
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="input-icon-end position-relative">
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "5px",
                                        }}
                                    >
                                        <label style={{ color: "#000" }}>Date Range</label>
                                        <PredefinedDateRanges state={states} setState={setState} />
                                    </div>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        margin: "20px 0px 0px 6rem",
                                    }}
                                >
                                    <div className="dropdown me-3">
                                        <Link
                                            style={{
                                                background: "#3ab54bbf",
                                                color: "#fff",
                                            }}
                                            to="#"
                                            className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                                            data-bs-toggle="dropdown"
                                        >
                                            Export
                                        </Link>

                                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                                            <li>
                                                <Link to="#" className="dropdown-item rounded-1">
                                                    <i className="ti ti-file-type-pdf me-1" />
                                                    Export as PDF
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="#" className="dropdown-item rounded-1">
                                                    <i className="ti ti-file-type-xls me-1" />
                                                    Export as Excel{" "}
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body pt-20">
                            <div className="row mb-3">
                                <div className="col-md-4" style={{ paddingBottom: 10 }}>
                                    <div
                                        style={{
                                            background: "linear-gradient(135deg, #2196F3, #1565C0)",
                                            borderRadius: "12px",
                                            padding: "15px",
                                            color: "#fff",
                                            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <h6 style={{ opacity: 0.8 }}>Total Capital</h6>
                                        <h3 style={{ fontWeight: "bold", margin: 0 }}>
                                            ₹ {getTotalCapital().toFixed(2)}
                                        </h3>
                                    </div>
                                </div>

                                {/* Total Loan */}
                                <div className="col-md-4">
                                    <div
                                        style={{
                                            background: "linear-gradient(135deg, #FF9800, #EF6C00)",
                                            borderRadius: "12px",
                                            padding: "15px",
                                            color: "#fff",
                                            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <h6 style={{ opacity: 0.8 }}>Total Loan</h6>
                                        <h3 style={{ fontWeight: "bold", margin: 0 }}>
                                            ₹ {getTotalLoan().toFixed(2)}
                                        </h3>
                                    </div>
                                </div>

                                {/* Total Interest */}
                                <div className="col-md-4">
                                    <div
                                        style={{
                                            background: "linear-gradient(135deg, #4CAF50, #2E7D32)",
                                            borderRadius: "12px",
                                            padding: "15px",
                                            color: "#fff",
                                            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <h6 style={{ opacity: 0.8 }}>
                                            Total Interest {start && end && "(Filtered)"}
                                        </h6>
                                        <h3 style={{ fontWeight: "bold", margin: 0 }}>
                                            ₹ {getTotalInterest().toFixed(2)}
                                        </h3>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div style={{ background: "#673AB7", borderRadius: "12px", padding: "15px", color: "#fff" }}>
                                        <h6>Paid Interest</h6>
                                        <h3>₹ {getTotalPaidInterest().toFixed(2)}</h3>
                                    </div>
                                </div>

                                {/* Balance Interest */}
                                <div className="col-md-4">
                                    <div style={{ background: "#E91E63", borderRadius: "12px", padding: "15px", color: "#fff" }}>
                                        <h6>Remaining Interest</h6>
                                        <h3>₹ {getTotalBalanceInterest().toFixed(2)}</h3>
                                    </div>
                                </div>

                                {/* Remaining Loan */}
                                <div className="col-md-4">
                                    <div style={{ background: "#009688", borderRadius: "12px", padding: "15px", color: "#fff" }}>
                                        <h6>Remaining Loan</h6>
                                        <h3>₹ {getTotalRemainingLoan().toFixed(2)}</h3>
                                    </div>
                                </div>
                            </div>
                            {allCapitalData && allCapitalData.length > 0 && (
                                <Table
                                    dataSource={allCapitalData}
                                    columns={columns}
                                    Selection={true}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {showPaymentModal && (
                <div
                    className="modal show d-block"
                    style={{ background: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Payment Details</h5>
                                <button
                                    className="btn-close"
                                    onClick={() => setShowPaymentModal(false)}
                                />
                            </div>

                            <div className="modal-body">
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Date</th>
                                            <th>Type</th>
                                            <th>Amount</th>
                                            <th>TDS</th>
                                            <th>Net</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(payments) && payments.length > 0 ? (
                                            payments.map((p, i) => (
                                                <tr key={i}>
                                                    <td>{i + 1}</td>
                                                    <td>{moment(p.date_time).format("DD-MM-YYYY")}</td>
                                                    <td>{p.paymentType}</td>
                                                    <td>₹ {p.amount}</td>
                                                    <td>{p.paymentType === "interest" ? `₹ ${p.tds || 0}` : "-"}</td>
                                                    <td>
                                                        {p.paymentType === "interest"
                                                            ? `₹ ${p.netAmount || (p.amount - (p.tds || 0))}`
                                                            : `₹ ${p.amount}`}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="text-center">
                                                    No Payments Found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAddPaymentModal && (
                <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-md modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5>Add Payment</h5>
                                <button
                                    className="btn-close"
                                    onClick={() => setShowAddPaymentModal(false)}
                                />
                            </div>

                            <div className="modal-body">
                                {/* <input type="number" placeholder="Amount" className="form-control mb-2" />
                                <select className="form-control mb-2">
                                    <option value="interest">Interest</option>
                                    <option value="principal">Principal</option>
                                </select>
                                <input type="date" className="form-control" /> */}
                                <input
                                    type="number"
                                    name="amount"
                                    value={paymentForm.amount}
                                    onChange={handlePaymentChange}
                                    placeholder="Amount"
                                    className="form-control mb-2"
                                />

                                <select
                                    name="paymentType"
                                    value={paymentForm.paymentType}
                                    onChange={handlePaymentChange}
                                    className="form-control mb-2"
                                >
                                    <option value="capital">Capital</option>
                                    <option value="loan">Loan</option>
                                    <option value="interest">Interest</option>
                                </select>

                                <input
                                    type="date"
                                    name="date_time"
                                    value={paymentForm.date_time}
                                    onChange={handlePaymentChange}
                                    className="form-control"
                                />
                                {paymentForm.paymentType === "interest" && paymentForm.amount && (
                                    <div
                                        style={{
                                            background: "#f8f9fa",
                                            border: "1px solid #ddd",
                                            borderRadius: "8px",
                                            padding: "10px",
                                            marginTop: "10px",
                                        }}
                                    >
                                        {(() => {
                                            const amount = Number(paymentForm.amount || 0);
                                            const tds = (amount * 10) / 100;
                                            const net = amount - tds;

                                            return (
                                                <>
                                                    <div className="d-flex justify-content-between">
                                                        <span>Gross Amount</span>
                                                        <strong>₹ {amount.toFixed(2)}</strong>
                                                    </div>

                                                    <div className="d-flex justify-content-between text-danger">
                                                        <span>TDS (10%)</span>
                                                        <strong>₹ {tds.toFixed(2)}</strong>
                                                    </div>

                                                    <div className="d-flex justify-content-between text-success">
                                                        <span>Net Payable</span>
                                                        <strong>₹ {net.toFixed(2)}</strong>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}


                            </div>
                            <div>
                                <button className="btn btn-primary" onClick={handleSavePayment}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            )}

            {showAddCapitalModal && (
                <div
                    className="modal show d-block"
                    style={{ background: "rgba(0,0,0,0.5)", zIndex: 9999 }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">

                            {/* Header */}
                            <div className="modal-header">
                                <h5>Add Capital / Loan</h5>
                                <button
                                    className="btn-close"
                                    onClick={() => setShowAddCapitalModal(false)}
                                />
                            </div>

                            {/* Body */}
                            <div className="modal-body">
                                <div className="row">

                                    {/* Capital */}
                                    <div className="col-md-6 mb-3">
                                        <label>Capital *</label>
                                        <input
                                            type="number"
                                            name="amount"
                                            value={formData.capital}
                                            onChange={handleChange}
                                            placeholder="Enter Capital"
                                            className="form-control"
                                        />
                                    </div>

                                    {/* Loan Amount */}
                                    <div className="col-md-6 mb-3">
                                        <label>Loan Amount *</label>
                                        <input
                                            type="number"
                                            name="loanAmount"
                                            value={formData.loanAmount}
                                            onChange={handleChange}
                                            placeholder="Enter Loan Amount"
                                            className="form-control"
                                        />
                                    </div>

                                    {/* Loan Date */}
                                    <div className="col-md-6 mb-3">
                                        <label>Loan Date *</label>
                                        <input
                                            type="date"
                                            name="loanDate"
                                            value={formData.loanDate}
                                            onChange={handleChange}
                                            className="form-control"
                                        />
                                    </div>

                                    {/* Interest */}
                                    <div className="col-md-6 mb-3">
                                        <label>Loan Interest Rate (%) *</label>
                                        <input
                                            type="number"
                                            name="loanInterestRate"
                                            value={formData.loanInterestRate}
                                            onChange={handleChange}
                                            placeholder="Enter Interest Rate"
                                            className="form-control"
                                        />
                                    </div>

                                </div>
                            </div>

                            {/* Footer */}
                            <div className="modal-footer">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setShowAddCapitalModal(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="btn btn-primary"
                                    onClick={(event) => handleSubmit(event as any)}
                                >
                                    Save
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}
            {successCreation && <EmpSuccess all_routes={all_routes} />}
        </>
    );
};

export default DirectorCapital;
